import { memo, useEffect, useState } from "react"
import { API } from "../../../../components/API"
import { extractDateTime } from "../../../../components/functions/extractDateTime"
import Skeleton from "../../../../UI/components/UIKit/Skeleton"
import styled from "styled-components"
import { IAchivement } from "../../../../components/types/achievements"
import { MAIN_COLORS } from "../../../../UI/consts/colors"
import Modal from "../../../../UI/components/UIKit/Modal"

const Wrapper = styled.div<{$achieved: boolean}>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background: ${MAIN_COLORS.gradient_purple_200};
    border-radius: 12px;
    padding: 8px;

    img {
        width: 35vw;
        height: 35vw;
        ${p => !p.$achieved && `
            opacity: 0.2;
        `}
    }

    h1 {
      background: ${MAIN_COLORS.gradient_yellow_pink};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    h5 {
        color: ${MAIN_COLORS.purple_700};
    }
`

const Column = styled.div`
    display: flex ;
    flex-direction: column;
    gap: 6px;
    text-align: center;
`

const ProgressBarTrack = styled.div`
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: ${MAIN_COLORS.purple_200};
    overflow: hidden;
    margin-top: 6px;
`;

const ProgressBarFill = styled.div<{ percent: number; complete: boolean }>`
    height: 100%;
    border-radius: 2px;
    background: ${p => p.complete ? MAIN_COLORS.yellow : MAIN_COLORS.gradient_yellow_pink};
    width: ${p => Math.min(p.percent, 100)}%;
    transition: width 0.4s ease-out;
    ${p => p.complete && `box-shadow: 0 0 4px ${MAIN_COLORS.yellow}80;`}
`;

const ProgressLabel = styled.span`
    font-size: 10px;
    color: ${MAIN_COLORS.purple_800};
    margin-top: 2px;
`;

const ActionButton = styled.button`
    width: 100%;
    margin-top: 12px;
    padding: 12px;
    border: none;
    border-radius: 10px;
    background: ${MAIN_COLORS.yellow};
    color: ${MAIN_COLORS.purple_100};
    font-weight: 700;
`;

const AchieveItem = ({ data }: { data:IAchivement }) => {
    const [ img, setImg ] = useState('');
    const [ imgIsLoading, setImgIsLoading ] = useState(true);
    const [ modalIsOpen, setModalIsOpen ] = useState(false);

    const getImage = async () => {
      const res = await API.getImageData(data.id,'achievements','unmasked');
      if(res) setImg(res);
    }

    useEffect( ()=>{
      getImage().finally(() => setImgIsLoading(false));
    },[])

    const hasProgress = data.threshold > 1;
    const progressPercent = hasProgress ? (data.progress / data.threshold) * 100 : 0;
    const isComplete = hasProgress && data.progress >= data.threshold;

    const openAction = () => {
      if (!data.action_url) return;

      if (data.action_url.startsWith('https://t.me/')) {
        window.Telegram.WebApp.openTelegramLink(data.action_url);
        return;
      }

      window.Telegram.WebApp.openLink(data.action_url);
    };

    return(
      <>
        <Modal isOpen={modalIsOpen} close={() => setModalIsOpen(false)}>
          <Wrapper $achieved={data.is_achieved}>
            <img
              src={img}
              alt=''
            />
            <h1>{data.name}</h1>
            <h4>{data.description}</h4>
            {hasProgress && (
              <>
                <ProgressBarTrack>
                  <ProgressBarFill percent={progressPercent} complete={isComplete} />
                </ProgressBarTrack>
                <ProgressLabel>
                  {isComplete ? 'Выполнено!' : `${data.progress} из ${data.threshold}`}
                </ProgressLabel>
              </>
            )}
            {data.unlocked_at && <h5>Дата получения: {extractDateTime(data.unlocked_at)}</h5>}
            {!data.is_achieved && data.action_url && (
              <ActionButton onClick={openAction}>Выполнить</ActionButton>
            )}
          </Wrapper>
        </Modal>
        <Wrapper $achieved={data.is_achieved} onClick={() => setModalIsOpen(true)}>
            {imgIsLoading
              ? <div className='caracter'>
                  <Skeleton height={'35vw'} width={'35vw'} />
                </div>
              :
                <img
                  src={img}
                  alt=''
                />
            }
            <Column>
                <h4>{data.name}</h4>
                <h5>{data.description}</h5>
                {hasProgress && !isComplete && (
                  <>
                    <ProgressBarTrack>
                      <ProgressBarFill percent={progressPercent} complete={false} />
                    </ProgressBarTrack>
                    <ProgressLabel>{data.progress} из {data.threshold}</ProgressLabel>
                  </>
                )}
            </Column>
        </Wrapper>
      </>
    )
}

export default memo(AchieveItem)
